import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  EnumHelper,
  FailureReasonBaseData,
  FilterRange,
  IdNameData,
  NumberHelper,
  ObjectHelper,
  TransactionNextStatus,
  TransactionStatus,
  TransactionType
} from 'common';
import moment from "moment";
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AccountService } from '../../account/account.service';
import { PromotionSelectOptionData } from '../../admin/promotion/models/promotion.model';
import { TenantService } from '../../admin/tenant/tenant.service';
import { readOnly } from '../../user/user-permission/user-permission.data';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import {
  TransactionFilters,
  TransactionListComponentData,
  TransactionQueryParams
} from '../transaction.model';

@Component({
    selector: 'ifb-transaction-list-filter-dialog',
    templateUrl: './transaction-list-filter-dialog.component.html',
    standalone: false
})

export class TransactionListFilterDialogComponent implements OnInit {
  statusOptions = EnumHelper.getNamesAndStringValues(TransactionStatus);
  nextStatusOptions = EnumHelper.getNamesAndValues(TransactionNextStatus);
  transactionTypeOptions = EnumHelper.getNamesAndStringValues(TransactionType);
  form: UntypedFormGroup;
  hiddenTransactionColumns: boolean;
  companies: IdNameData[] = [];
  facilities: IdNameData[] = [];
  canReadFacilities: boolean;

  readPromotions = false;

  promotionOptions: PromotionSelectOptionData[];
  promotionOptionsMap: Map<string, string>;

  failureReasonsOptions: FailureReasonBaseData[];
  

  constructor(
    public dialogRef: MatDialogRef<TransactionListFilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: UntypedFormBuilder,
    private tenantService: TenantService,
    private accountService: AccountService,
    private userPermissionService: UserPermissionService
  ) {

    this.promotionOptions = data.promotionsToSelect;
    this.promotionOptionsMap = new Map(data.promotionsToSelect?.map((r): [string, string] => [r.id, r.name]));

    this.failureReasonsOptions = data.failureReasonsOptions;

    this.form = this.formBuilder.group({
      loanNumber: this.data.query.loanNumber || '',
      loanFacilityId: this.data.query.loanFacilityId || '',
      businessName: this.data.query.businessName || '',
      id: [this.data.query.id || undefined, Validators.max(NumberHelper.MaxValue)],
      companyId: +this.data.query.companyId || undefined,
      type: { value: ObjectHelper.makeArray(this.data.query.type), disabled: false },
      status: { value: ObjectHelper.makeArray(this.data.query.status), disabled: false },
      nextStatus: this.data.query.nextStatus || undefined,

      amountFrom: this.data.query.amountFrom || undefined,
      amountTo: this.data.query.amountTo || undefined,

      creditLimitFrom: this.data.query.creditLimitFrom || undefined,
      creditLimitTo: this.data.query.creditLimitTo || undefined,

      promotionIds: { value: ObjectHelper.makeArray(this.data.query.promotionIds), disabled: false },
      failureReasons: { value: ObjectHelper.makeArray(this.data.query.failureReasons), disabled: false },

      transactionDate: this.data.query.transactionDate || undefined,
      fromDate: new UntypedFormControl(this.data.query.fromDate ? new Date(this.data.query.fromDate) : undefined, { validators: null, updateOn: 'blur' }),
      toDate: new UntypedFormControl(this.data.query.toDate ? new Date(this.data.query.toDate) : undefined, { validators: null, updateOn: 'blur' }),

      processDate: this.data.query.processDate || undefined,
      processDateFrom: new UntypedFormControl(this.data.query.processDateFrom ? new Date(this.data.query.processDateFrom) : undefined, { validators: null, updateOn: 'blur' }),
      processDateTo: new UntypedFormControl(this.data.query.processDateTo ? new Date(this.data.query.processDateTo) : undefined, { validators: null, updateOn: 'blur' }),

      principalAmount: this.data.query.principalAmount || undefined,
      principalAmountExcept: this.data.query.principalAmountExcept,
      principalAmountFrom: new UntypedFormControl(this.data.query.principalAmountFrom, { validators: null, updateOn: 'blur' }),
      principalAmountTo: new UntypedFormControl(this.data.query.principalAmountTo, { validators: null, updateOn: 'blur' }),

      interestAmount: this.data.query.interestAmount || undefined,
      interestAmountExcept: this.data.query.interestAmountExcept,
      interestAmountFrom: new UntypedFormControl(this.data.query.interestAmountFrom, { validators: null, updateOn: 'blur' }),
      interestAmountTo: new UntypedFormControl(this.data.query.interestAmountTo, { validators: null, updateOn: 'blur' }),

      deferredFeeAmount: this.data.query.deferredFeeAmount || undefined,
      deferredFeeAmountExcept: this.data.query.deferredFeeAmountExcept,
      deferredFeeAmountFrom: new UntypedFormControl(this.data.query.deferredFeeAmountFrom, { validators: null, updateOn: 'blur' }),
      deferredFeeAmountTo: new UntypedFormControl(this.data.query.deferredFeeAmountTo, { validators: null, updateOn: 'blur' }),

      cashFeeAmount: this.data.query.cashFeeAmount || undefined,
      cashFeeAmountExcept: this.data.query.cashFeeAmountExcept,
      cashFeeAmountFrom: new UntypedFormControl(this.data.query.cashFeeAmountFrom, { validators: null, updateOn: 'blur' }),
      cashFeeAmountTo: new UntypedFormControl(this.data.query.cashFeeAmountTo, { validators: null, updateOn: 'blur' }),

      disbursedAmount: this.data.query.disbursedAmount || undefined,
      disbursedAmountExcept: this.data.query.disbursedAmountExcept ,
      disbursedAmountFrom: new UntypedFormControl(this.data.query.disbursedAmountFrom, { validators: null, updateOn: 'blur' }),
      disbursedAmountTo: new UntypedFormControl(this.data.query.disbursedAmountTo, { validators: null, updateOn: 'blur' }),
    });

    this.userPermissionService.granted([readOnly('servicing-promotions')]).subscribe(res => {
      this.readPromotions = res;
      if(!res)
        this.form.controls.promotionIds.disable({onlySelf: false, emitEvent: false});
    })
  }

  ngOnInit(): void {
    this.tenantService.names().subscribe(data => this.companies = data);

    this.userPermissionService 
      .granted([readOnly("admin-facilities")])
      .pipe(
        tap(permission => this.canReadFacilities = permission),
        switchMap(permission => 
          permission 
            ? this.accountService.getFacilitiesOptions() 
            : of([] as IdNameData[])
        )
      )
      .subscribe(options => {
        this.facilities = options;
      });

    if (this.data.routeData.account && this.data.routeData.accountInfo) {
      this.hiddenTransactionColumns = true;
    }
  }

  // eslint-disable-next-line max-len
  static show(dialog: MatDialog, query: TransactionQueryParams, routeData: TransactionListComponentData, canFilterByLoan: boolean, failureReasonsOptions: FailureReasonBaseData[]): Observable<any> {
    return dialog.open(TransactionListFilterDialogComponent, {
      data: { 
        query: query, 
        routeData: routeData, 
        canFilterByLoan: canFilterByLoan, 
        promotionsToSelect: routeData.promotionsToSelect,
        failureReasonsOptions: failureReasonsOptions 
      },
      closeOnNavigation: true,
    }).afterClosed();
  }

  transactionDateSelected(dataRange: FilterRange<moment.Moment>): void {
    this.form.patchValue({
      fromDate: dataRange.from,
      toDate: dataRange.to
    });
  }

  processDateSelected(dataRange: FilterRange<moment.Moment>): void {
    this.form.patchValue({
      processDateFrom: dataRange.from,
      processDateTo: dataRange.to
    });
  }


  principalAmountSelected(amountRange: FilterRange<number>): void {
    this.form.patchValue({
      principalAmountExcept: amountRange.except,
      principalAmountFrom: amountRange.from,
      principalAmountTo: amountRange.to
    });
  }

  interestAmountSelected(amountRange: FilterRange<number>): void {
    this.form.patchValue({
      interestAmountExcept: amountRange.except,
      interestAmountFrom: amountRange.from,
      interestAmountTo: amountRange.to
    });
  }

  deferredFeeAmountSelected(amountRange: FilterRange<number>): void {
    this.form.patchValue({
      deferredFeeAmountExcept: amountRange.except,
      deferredFeeAmountFrom: amountRange.from,
      deferredFeeAmountTo: amountRange.to
    });
  }

  cashFeeAmountSelected(amountRange: FilterRange<number>): void {
    this.form.patchValue({
      cashFeeAmountExcept: amountRange.except,
      cashFeeAmountFrom: amountRange.from,
      cashFeeAmountTo: amountRange.to
    });
  }

  disbursedAmountSelected(amountRange: FilterRange<number>): void {
    this.form.patchValue({
      disbursedAmountExcept: amountRange.except,
      disbursedAmountFrom: amountRange.from,
      disbursedAmountTo: amountRange.to
    });
  }

  onConfirmed(formValues: unknown): void {
    this.dialogRef.close(new TransactionFilters(formValues));
  }
}
